import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
  FolderOpen,
  FileText,
  ExternalLink,
  CheckCircle2,
  Clock,
  DollarSign,
  Target,
  Eye,
  EyeOff,
  Layers,
  Split,
  Plus,
  Trash2,
  FileSpreadsheet,
  Download,
  Check,
  CreditCard,
  Copy,
  Link2,
  Send,
  AlertTriangle,
  Palette,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";

// Generate month options
const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = -6; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { year: "numeric", month: "short" });
    months.push({ value, label });
  }
  return months;
};

// Unified project statuses
const DEAL_STATUSES = [
  { value: "proposal_sent", label: "Proposal", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  { value: "contract_negotiation", label: "Negotiation", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { value: "contract_signed", label: "Signed", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  { value: "sent_to_pool", label: "In Pool", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  { value: "in_progress", label: "In Progress", color: "bg-sky-500/20 text-sky-400 border-sky-500/30" },
  { value: "awaiting_client", label: "Awaiting Client", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
  { value: "awaiting_payment", label: "Awaiting Payment", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  { value: "completed", label: "Completed", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
];

const TrpSalesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { displayCurrency, getCurrencySymbol, rates } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [salesServices, setSalesServices] = useState({}); // payments by project_id
  
  // Split dialog state
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);
  const [splitService, setSplitService] = useState(null);
  const [splitParts, setSplitParts] = useState([
    { percent: 50, amount: 0, month: "" },
    { percent: 50, amount: 0, month: "" },
  ]);
  const [splitting, setSplitting] = useState(false);
  
  // Invoice generation state
  const [selectedPayments, setSelectedPayments] = useState({});
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  
  // Stripe payment
  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
  const [lastInvoiceId, setLastInvoiceId] = useState(null);
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState("");
  const [stripeCheckoutUrl, setStripeCheckoutUrl] = useState("");
  const [generatingStripeLink, setGeneratingStripeLink] = useState(false);
  
  // Month selection - "all" or specific month
  const [selectedMonth, setSelectedMonth] = useState("all");
  
  // Studio Request dialog (send request to partner)
  const [studioRequestDialogOpen, setStudioRequestDialogOpen] = useState(false);
  const [studioRequestProject, setStudioRequestProject] = useState(null);
  const [studioRequestForm, setStudioRequestForm] = useState({
    request_type: "price_adjustment",
    title: "",
    description: "",
    amount: "",
    priority: "normal",
  });
  const [submittingStudioRequest, setSubmittingStudioRequest] = useState(false);
  const monthOptions = useMemo(() => getMonthOptions(), []);

  const usdToUah = rates?.USD || 41.5;
  const eurToUah = rates?.EUR || 43.5;

  // Convert amount to USD for total calculation
  const convertToUSD = (amount, currency) => {
    if (!amount) return 0;
    if (currency === "USD") return amount;
    if (currency === "UAH") return amount / usdToUah;
    if (currency === "EUR") return amount * (eurToUah / usdToUah);
    return amount;
  };

  // TRP Invoices list
  const [invoices, setInvoices] = useState([]);
  const [showInvoices, setShowInvoices] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Fetch projects
      const projectsRes = await api.get("/trp/projects");
      const projectsList = projectsRes.data || [];
      setProjects(projectsList);
      
      // Fetch invoices
      try {
        const invoicesRes = await api.get("/trp/invoices?limit=20");
        setInvoices(invoicesRes.data || []);
      } catch (e) {
        console.log("No invoices yet");
      }
      
      // Fetch sales_services (real payments) for all TRP projects
      // Request services for each TRP project
      const trpProjectIds = projectsList.map(p => p.id);
      const servicesByProject = {};
      
      // Fetch in batches for each project
      await Promise.all(
        trpProjectIds.map(async (projectId) => {
          try {
            const res = await api.get(`/sales-services?project_id=${projectId}`);
            const services = res.data || [];
            if (services.length > 0) {
              servicesByProject[projectId] = services;
            }
          } catch (e) {
            // Skip if no services
          }
        })
      );
      
      setSalesServices(servicesByProject);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load TRP projects");
    } finally {
      setLoading(false);
    }
  };

  const toUAH = (amount, currency) => {
    if (!amount) return 0;
    if (currency === "UAH") return amount;
    if (currency === "USD") return amount * usdToUah;
    if (currency === "EUR") return amount * eurToUah;
    return amount;
  };

  const formatAmount = (amountUAH) => {
    let converted = amountUAH;
    if (displayCurrency === "USD") converted = amountUAH / usdToUah;
    else if (displayCurrency === "EUR") converted = amountUAH / eurToUah;
    const symbol = getCurrencySymbol();
    return `${symbol}${converted.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const formatOriginal = (amount, currency) => {
    const symbols = { USD: "$", EUR: "€", UAH: "₴" };
    return `${symbols[currency] || "$"}${amount?.toLocaleString() || 0}`;
  };

  const formatMonth = (monthStr) => {
    if (monthStr === "all") return "All Projects";
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    return `${monthNames[month - 1]} ${year}`;
  };

  const formatMonthShort = (monthStr) => {
    if (!monthStr || monthStr === "-") return "-";
    const [year, month] = monthStr.split("-").map(Number);
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${monthNames[month - 1]} ${year}`;
  };

  const navigateMonth = (direction) => {
    if (selectedMonth === "all") {
      // From "all" go to current month
      const now = new Date();
      setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
      return;
    }
    const [year, month] = selectedMonth.split("-").map(Number);
    let newMonth = month + direction;
    let newYear = year;
    if (newMonth > 12) { newMonth = 1; newYear++; }
    else if (newMonth < 1) { newMonth = 12; newYear--; }
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, "0")}`);
  };

  const getStatusBadge = (status) => {
    const statusInfo = DEAL_STATUSES.find(s => s.value === status) || { label: status, color: "bg-zinc-500/20 text-zinc-400" };
    return (
      <Badge className={cn("text-xs border", statusInfo.color)}>
        {statusInfo.label}
      </Badge>
    );
  };

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  // Toggle payment paid status (using sales_services). Patch local state instead
  // of refetching so the page doesn't visibly reload on every checkbox click.
  const togglePaymentPaid = async (serviceId, isPaid) => {
    const paidDate = isPaid ? new Date().toISOString().split("T")[0] : null;
    const previous = salesServices;
    const patched = Object.fromEntries(
      Object.entries(salesServices).map(([pid, services]) => [
        pid,
        services.map((s) => (s.id === serviceId ? { ...s, is_paid: isPaid, paid_date: paidDate } : s)),
      ])
    );
    setSalesServices(patched);
    try {
      await api.put(`/sales-services/${serviceId}`, {
        is_paid: isPaid,
        paid_date: paidDate,
      });
      toast.success(isPaid ? "Marked as paid" : "Marked as unpaid");
    } catch (error) {
      setSalesServices(previous);
      toast.error("Failed to update payment status");
    }
  };

  // Update payment month (using sales_services). Patch local state instead of refetching
  // the whole page so the dropdown doesn't trigger a visible reload.
  const updatePaymentMonth = async (serviceId, newMonth) => {
    const previous = salesServices;
    const patched = Object.fromEntries(
      Object.entries(salesServices).map(([pid, services]) => [
        pid,
        services.map((s) => (s.id === serviceId ? { ...s, month: newMonth } : s)),
      ])
    );
    setSalesServices(patched);
    try {
      await api.put(`/sales-services/${serviceId}`, { month: newMonth });
      toast.success("Month updated");
    } catch (error) {
      setSalesServices(previous);
      toast.error("Failed to update month");
    }
  };

  // Open split dialog
  const handleOpenSplitDialog = (service) => {
    setSplitService(service);
    const totalAmount = service.amount || 0;
    setSplitParts([
      { percent: 50, amount: Math.round(totalAmount * 0.5), month: service.month || "" },
      { percent: 50, amount: Math.round(totalAmount * 0.5), month: service.month || "" },
    ]);
    setSplitDialogOpen(true);
  };

  // Update split part
  const updateSplitPart = (index, field, value) => {
    const newParts = [...splitParts];
    const totalAmount = splitService?.amount || 0;
    
    if (field === "percent") {
      newParts[index].percent = Number(value) || 0;
      newParts[index].amount = Math.round(totalAmount * (newParts[index].percent / 100));
    } else if (field === "amount") {
      newParts[index].amount = Number(value) || 0;
      newParts[index].percent = totalAmount > 0 ? Math.round((newParts[index].amount / totalAmount) * 100) : 0;
    } else {
      newParts[index][field] = value;
    }
    setSplitParts(newParts);
  };

  // Add split part
  const addSplitPart = () => {
    setSplitParts([...splitParts, { percent: 0, amount: 0, month: splitService?.month || "" }]);
  };

  // Remove split part
  const removeSplitPart = (index) => {
    if (splitParts.length <= 2) return;
    setSplitParts(splitParts.filter((_, i) => i !== index));
  };

  // Execute split
  const handleSplit = async () => {
    if (!splitService) return;
    
    const totalSplitAmount = splitParts.reduce((sum, p) => sum + (p.amount || 0), 0);
    const originalAmount = splitService.amount || 0;
    
    if (Math.abs(totalSplitAmount - originalAmount) > 1) {
      toast.error(`Split amounts (${totalSplitAmount}) must equal original (${originalAmount})`);
      return;
    }
    
    try {
      setSplitting(true);
      await api.post(`/sales-services/${splitService.id}/split-existing`, {
        parts: splitParts.map((p, i) => ({
          name: `${splitService.name} (${i + 1}/${splitParts.length})`,
          amount: p.amount,
          month: p.month,
        })),
      });
      toast.success(`Split into ${splitParts.length} payments`);
      setSplitDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to split payment");
    } finally {
      setSplitting(false);
    }
  };

  // ===== INVOICE FUNCTIONS =====
  
  // Toggle payment selection
  const togglePaymentSelection = (paymentId) => {
    setSelectedPayments(prev => ({
      ...prev,
      [paymentId]: !prev[paymentId]
    }));
  };

  // Select all payments for a month (or all if month === "all")
  const selectAllMonth = (month) => {
    const newSelected = { ...selectedPayments };
    projects.forEach(project => {
      const payments = salesServices[project.id] || [];
      payments.forEach(payment => {
        // Skip already-paid services — they aren't eligible for a new invoice.
        if (payment.is_paid) return;
        // Match by month or select all if "all" is chosen
        const shouldSelect = month === "all" || payment.month === month;
        if (shouldSelect) {
          const paymentId = `${project.id}_${payment.id}`;
          newSelected[paymentId] = true;
        }
      });
    });
    setSelectedPayments(newSelected);
  };

  // Clear all selections
  const clearSelections = () => {
    setSelectedPayments({});
  };

  // Get selected payment IDs for invoice
  const getSelectedPaymentIds = () => {
    return Object.entries(selectedPayments)
      .filter(([_, selected]) => selected)
      .map(([id]) => {
        // Extract actual payment ID from "projectId_paymentId" format
        const parts = id.split("_");
        if (parts.length >= 2) {
          return parts[1]; // Return just the payment ID
        }
        return id;
      });
  };

  // Calculate total for selected payments
  const selectedTotal = useMemo(() => {
    let total = 0;
    Object.entries(selectedPayments).forEach(([id, selected]) => {
      if (selected) {
        const [projectId, paymentId] = id.split("_");
        const payments = salesServices[projectId] || [];
        const payment = payments.find(p => p.id === paymentId);
        if (payment) {
          // Find project to get currency
          const project = projects.find(p => p.id === projectId);
          const currency = project?.currency || payment.currency || "USD";
          // Convert to USD inline
          let usdAmount = payment.amount || 0;
          if (currency === "UAH") usdAmount = usdAmount / usdToUah;
          else if (currency === "EUR") usdAmount = usdAmount * (eurToUah / usdToUah);
          total += usdAmount;
        }
      }
    });
    return Math.round(total);
  }, [selectedPayments, salesServices, projects, usdToUah, eurToUah]);

  // Generate invoice - partner is determined automatically from TRP projects
  const handleGenerateInvoice = async () => {
    const paymentIds = getSelectedPaymentIds();
    
    if (paymentIds.length === 0) {
      toast.error("Выберите платежи для инвойса");
      return;
    }
    
    try {
      setGeneratingInvoice(true);
      const response = await api.post("/trp/generate-invoice", {
        payment_ids: paymentIds
      });
      
      // Download PDF
      const { pdf_base64, invoice_number, invoice_id } = response.data;
      const blob = new Blob(
        [Uint8Array.from(atob(pdf_base64), c => c.charCodeAt(0))],
        { type: "application/pdf" }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Инвойс ${invoice_number} создан и скачан`);
      setInvoiceDialogOpen(false);
      clearSelections();
      
      // Store invoice_id for Stripe payment link
      setLastInvoiceId(invoice_id);
      setLastInvoiceNumber(invoice_number);
      setStripeDialogOpen(true);
    } catch (error) {
      console.error("Invoice error:", error);
      toast.error(error.response?.data?.detail || "Ошибка создания инвойса");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Generate Stripe payment link
  const handleCreateStripeLink = async () => {
    if (!lastInvoiceId) return;
    
    try {
      setGeneratingStripeLink(true);
      const response = await api.post("/stripe/create-checkout", {
        invoice_id: lastInvoiceId,
        origin_url: window.location.origin
      });
      
      setStripeCheckoutUrl(response.data.checkout_url);
      toast.success("Ссылка на оплату создана");
      
      // Refresh invoices to show new link
      try {
        const invoicesRes = await api.get("/trp/invoices?limit=20");
        setInvoices(invoicesRes.data || []);
      } catch (e) {}
    } catch (error) {
      console.error("Stripe error:", error);
      toast.error(error.response?.data?.detail || "Ошибка создания ссылки");
    } finally {
      setGeneratingStripeLink(false);
    }
  };

  // Copy Stripe link to clipboard
  const copyStripeLink = (url) => {
    if (url) {
      navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована");
    }
  };

  // Create Stripe link for existing invoice
  const createStripeLinkForInvoice = async (invoiceId) => {
    try {
      const response = await api.post("/stripe/create-checkout", {
        invoice_id: invoiceId,
        origin_url: window.location.origin
      });
      toast.success("Ссылка создана");
      
      // Refresh invoices
      const invoicesRes = await api.get("/trp/invoices?limit=20");
      setInvoices(invoicesRes.data || []);
      
      // Copy to clipboard
      navigator.clipboard.writeText(response.data.checkout_url);
      toast.success("Ссылка скопирована в буфер");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка создания ссылки");
    }
  };

  // Open studio request dialog for a project
  const openStudioRequestDialog = (project) => {
    setStudioRequestProject(project);
    setStudioRequestForm({
      request_type: "price_adjustment",
      title: "",
      description: "",
      amount: "",
      price_per_unit: "",
      quantity: "",
      discount: "",
      priority: "normal",
    });
    setStudioRequestDialogOpen(true);
  };

  // Create studio request (send to partner for approval)
  const handleCreateStudioRequest = async () => {
    if (!studioRequestProject || !studioRequestForm.title) {
      toast.error("Please fill in the required fields");
      return;
    }
    
    setSubmittingStudioRequest(true);
    try {
      await api.post("/studio-requests", {
        project_id: studioRequestProject.id,
        request_type: studioRequestForm.request_type,
        title: studioRequestForm.title,
        description: studioRequestForm.description,
        amount: studioRequestForm.amount ? parseFloat(studioRequestForm.amount) : null,
        price_per_unit: studioRequestForm.price_per_unit ? parseFloat(studioRequestForm.price_per_unit) : null,
        quantity: studioRequestForm.quantity ? parseInt(studioRequestForm.quantity) : null,
        discount: studioRequestForm.discount ? parseFloat(studioRequestForm.discount) : null,
        priority: studioRequestForm.priority,
      });
      toast.success("Request sent to partner");
      setStudioRequestDialogOpen(false);
      setStudioRequestProject(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create request");
    } finally {
      setSubmittingStudioRequest(false);
    }
  };

  // Process projects with payments from sales_services
  const { filteredProjects, monthTotals, completedCount } = useMemo(() => {
    let projectsToShow = [];
    let totalAllUAH = 0;
    let totalPaidUAH = 0;
    let totalPendingUAH = 0;
    let paidPaymentsCount = 0;
    let totalPaymentsCount = 0;
    let completedProjectsCount = 0;

    projects.forEach(project => {
      const currency = project.currency || "USD";
      // Get real payments from sales_services
      const payments = salesServices[project.id] || [];
      
      // Check if project is completed (all payments paid)
      const allPaid = payments.length > 0 && payments.every(p => p.is_paid);
      const isCompleted = project.status === "completed" || allPaid;
      
      if (isCompleted) completedProjectsCount++;
      
      // Filter payments by month if not "all"
      let paymentsToShow = payments;
      if (selectedMonth !== "all") {
        paymentsToShow = payments.filter(p => {
          // Use month field from sales_services
          return p.month === selectedMonth;
        });
      }
      
      // Skip if no payments match the filter (except "all" mode, show projects without payments too)
      if (selectedMonth !== "all" && paymentsToShow.length === 0) return;
      
      // Calculate totals for this project (from ALL its payments)
      const allPaymentsTotal = payments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const paidTotal = payments.filter(p => p.is_paid)
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      const pendingTotal = allPaymentsTotal - paidTotal;
      
      // For month view, calculate month-specific totals
      const monthTotal = paymentsToShow.reduce((sum, p) => sum + (p.amount || 0), 0);
      const monthPaid = paymentsToShow.filter(p => p.is_paid)
        .reduce((sum, p) => sum + (p.amount || 0), 0);
      const monthPending = monthTotal - monthPaid;
      
      // Add to global totals
      if (selectedMonth === "all") {
        totalAllUAH += toUAH(allPaymentsTotal, currency);
        totalPaidUAH += toUAH(paidTotal, currency);
        totalPendingUAH += toUAH(pendingTotal, currency);
        totalPaymentsCount += payments.length;
        paidPaymentsCount += payments.filter(p => p.is_paid).length;
      } else {
        totalAllUAH += toUAH(monthTotal, currency);
        totalPaidUAH += toUAH(monthPaid, currency);
        totalPendingUAH += toUAH(monthPending, currency);
        totalPaymentsCount += paymentsToShow.length;
        paidPaymentsCount += paymentsToShow.filter(p => p.is_paid).length;
      }
      
      // Skip completed projects if not showing them
      if (!showCompleted && isCompleted) return;
      
      projectsToShow.push({
        ...project,
        payments: paymentsToShow,
        allPayments: payments,
        totalAmount: allPaymentsTotal,
        paidAmount: paidTotal,
        pendingAmount: pendingTotal,
        monthTotal,
        monthPaid,
        monthPending,
        paidCount: payments.filter(p => p.is_paid).length,
        isCompleted,
      });
    });

    // Sort: active projects first, then by pending amount (descending)
    projectsToShow.sort((a, b) => {
      if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
      return b.pendingAmount - a.pendingAmount;
    });

    return {
      filteredProjects: projectsToShow,
      monthTotals: {
        allUAH: totalAllUAH,
        paidUAH: totalPaidUAH,
        pendingUAH: totalPendingUAH,
        projectsCount: projectsToShow.length,
        paymentsCount: totalPaymentsCount,
        paidCount: paidPaymentsCount,
      },
      completedCount: completedProjectsCount,
    };
  }, [projects, salesServices, selectedMonth, showCompleted, usdToUah, eurToUah]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="trp-sales-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">TRP Sales</h1>
          <p className="text-zinc-500 text-sm">Partner projects pipeline</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Artists button */}
          <Button
            variant="outline"
            onClick={() => navigate("/trp/artists")}
            className="border-zinc-700 text-zinc-300 hover:text-zinc-50 hover:border-zinc-500 text-xs"
            data-testid="view-artists-btn"
          >
            <Palette className="h-3.5 w-3.5 mr-1.5" />
            Artists
          </Button>
          {/* Add Project button */}
          <Button
            onClick={() => navigate("/trp/project/new")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
            data-testid="add-project-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Project
          </Button>
          
          {/* Invoice generation button - show when payments selected */}
          {Object.values(selectedPayments).some(v => v) && (
            <Button
              onClick={() => setInvoiceDialogOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white text-xs"
            >
              <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
              Invoice ({Object.values(selectedPayments).filter(v => v).length}) - ${selectedTotal.toLocaleString()}
            </Button>
          )}
          
          {/* Select all for month */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => selectAllMonth(selectedMonth)}
            className="text-xs border-zinc-700 text-zinc-400 hover:text-zinc-50"
          >
            <Check className="h-3.5 w-3.5 mr-1.5" />
            Select All
          </Button>
          
          {/* Clear selections */}
          {Object.values(selectedPayments).some(v => v) && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelections}
              className="text-xs border-zinc-700 text-zinc-400 hover:text-zinc-50"
            >
              Clear
            </Button>
          )}
          
          {/* All Projects button */}
          <Button
            variant={selectedMonth === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedMonth("all")}
            className={cn(
              "text-xs",
              selectedMonth === "all" 
                ? "bg-orange-500 hover:bg-orange-600 text-white" 
                : "border-zinc-700 text-zinc-400 hover:text-zinc-50"
            )}
          >
            <Layers className="h-3.5 w-3.5 mr-1.5" />
            All
          </Button>
          
          {/* Show invoices toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowInvoices(!showInvoices)}
            className={cn(
              "text-xs",
              showInvoices ? "bg-purple-900/30 border-purple-700 text-purple-400" : "border-zinc-700 text-zinc-400"
            )}
          >
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />
            Invoices ({invoices.length})
          </Button>
          
          {/* Show/Hide completed toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCompleted(!showCompleted)}
            className={cn(
              "text-xs",
              showCompleted ? "bg-green-900/30 border-green-700 text-green-400" : "border-zinc-700 text-zinc-400"
            )}
          >
            {showCompleted ? <Eye className="h-3.5 w-3.5 mr-1.5" /> : <EyeOff className="h-3.5 w-3.5 mr-1.5" />}
            Completed ({completedCount})
          </Button>
          
          {/* Month navigation */}
          <div className="flex items-center gap-2 bg-zinc-800/50 rounded-lg p-1">
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)} className="h-8 w-8">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-zinc-50 font-medium px-3 min-w-[140px] text-center">{formatMonth(selectedMonth)}</span>
            <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)} className="h-8 w-8">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-blue-400" />
              <span className="text-xs text-zinc-500 uppercase">Total</span>
            </div>
            <p className="text-xl font-bold text-blue-400">{formatAmount(monthTotals.allUAH)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-900/20 border-green-800/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-green-400" />
              <span className="text-xs text-green-500 uppercase">Paid</span>
            </div>
            <p className="text-xl font-bold text-green-400">{formatAmount(monthTotals.paidUAH)}</p>
            <p className="text-xs text-zinc-500">{monthTotals.paidCount} of {monthTotals.paymentsCount} payments</p>
          </CardContent>
        </Card>
        
        <Card className="bg-orange-900/20 border-orange-800/30">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-orange-400" />
              <span className="text-xs text-orange-500 uppercase">Pending</span>
            </div>
            <p className="text-xl font-bold text-orange-400">{formatAmount(monthTotals.pendingUAH)}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen className="h-4 w-4 text-purple-400" />
              <span className="text-xs text-zinc-500 uppercase">Projects</span>
            </div>
            <p className="text-xl font-bold text-purple-400">{monthTotals.projectsCount}</p>
            <p className="text-xs text-zinc-500">{monthTotals.paymentsCount} payments</p>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Section */}
      {showInvoices && invoices.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <FileSpreadsheet className="h-5 w-5 text-purple-400" />
              Созданные инвойсы
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">№ Инвойса</TableHead>
                  <TableHead className="text-zinc-400">Дата</TableHead>
                  <TableHead className="text-zinc-400 text-right">Сумма</TableHead>
                  <TableHead className="text-zinc-400 text-center">Статус оплаты</TableHead>
                  <TableHead className="text-zinc-400 text-center">Stripe Link</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map(invoice => (
                  <TableRow key={invoice.id} className="border-zinc-800 hover:bg-zinc-800/50">
                    <TableCell className="font-mono text-zinc-50">{invoice.invoice_number}</TableCell>
                    <TableCell className="text-zinc-400">{invoice.invoice_date || invoice.created_at?.split("T")[0]}</TableCell>
                    <TableCell className="text-right font-mono text-green-400">
                      ${invoice.total_amount?.toLocaleString() || "0"}
                    </TableCell>
                    <TableCell className="text-center">
                      {invoice.stripe_payment_status === "paid" ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">Оплачено</span>
                      ) : invoice.stripe_checkout_url ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400">Ожидает оплаты</span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-zinc-500/20 text-zinc-400">Не отправлено</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {invoice.stripe_checkout_url ? (
                        <div className="flex justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-purple-400 hover:text-purple-300"
                            onClick={() => copyStripeLink(invoice.stripe_checkout_url)}
                          >
                            <Copy className="h-3.5 w-3.5 mr-1" />
                            Копировать
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-zinc-400 hover:text-purple-400"
                            onClick={() => window.open(invoice.stripe_checkout_url, "_blank")}
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs border-purple-700 text-purple-400 hover:bg-purple-500/10"
                          onClick={() => createStripeLinkForInvoice(invoice.id)}
                        >
                          <CreditCard className="h-3.5 w-3.5 mr-1" />
                          Создать
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Projects Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          {filteredProjects.length === 0 ? (
            <div className="py-12 text-center text-zinc-500">
              {selectedMonth === "all" ? "No projects found" : `No projects with payments for ${formatMonth(selectedMonth)}`}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 w-[30px]"></TableHead>
                  <TableHead className="text-zinc-400">Project</TableHead>
                  <TableHead className="text-zinc-400">PM</TableHead>
                  <TableHead className="text-zinc-400 text-center">Status</TableHead>
                  <TableHead className="text-zinc-400 text-center w-[100px]">Month</TableHead>
                  <TableHead className="text-zinc-400 text-right">Total</TableHead>
                  <TableHead className="text-zinc-400 text-right">Paid</TableHead>
                  <TableHead className="text-zinc-400 text-right">Pending</TableHead>
                  <TableHead className="text-zinc-400 text-center">Payments</TableHead>
                  <TableHead className="text-zinc-400 w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map(project => {
                  const currency = project.currency || "USD";
                  const isExpanded = expandedProjects[project.id];
                  const hasPayments = project.payments.length > 0;
                  
                  return (
                    <React.Fragment key={project.id}>
                      {/* Project Row */}
                      <TableRow 
                        className={cn(
                          "border-zinc-800",
                          project.isCompleted ? "bg-green-950/10 hover:bg-green-950/20" : "hover:bg-zinc-800/50",
                          isExpanded && "bg-zinc-800/30"
                        )}
                      >
                        <TableCell 
                          className="w-[30px] cursor-pointer" 
                          onClick={() => hasPayments && toggleProject(project.id)}
                        >
                          {hasPayments && (
                            isExpanded 
                              ? <ChevronDown className="h-4 w-4 text-zinc-500" /> 
                              : <ChevronRightIcon className="h-4 w-4 text-zinc-500" />
                          )}
                        </TableCell>
                        <TableCell 
                          className="cursor-pointer" 
                          onClick={() => hasPayments && toggleProject(project.id)}
                        >
                          <div className="flex items-center gap-2">
                            {project.isCompleted && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                            <FolderOpen className={cn("h-4 w-4", project.isCompleted ? "text-green-400" : "text-sky-400")} />
                            <span className={cn("font-medium", project.isCompleted ? "text-green-400" : "text-zinc-50")}>
                              {project.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-zinc-400">
                          {project.project_manager_name || "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {getStatusBadge(project.status)}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-blue-400">
                            {formatOriginal(selectedMonth === "all" ? project.totalAmount : project.monthTotal, currency)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-green-400">
                            {formatOriginal(selectedMonth === "all" ? project.paidAmount : project.monthPaid, currency)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={cn(
                            "font-mono",
                            (selectedMonth === "all" ? project.pendingAmount : project.monthPending) > 0 ? "text-orange-400" : "text-zinc-500"
                          )}>
                            {formatOriginal(selectedMonth === "all" ? project.pendingAmount : project.monthPending, currency)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-zinc-400">
                          {selectedMonth === "all" 
                            ? `${project.paidCount}/${project.allPayments.length}`
                            : `${project.payments.filter(p => p.status === "paid" || p.is_paid).length}/${project.payments.length}`
                          }
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-zinc-400 hover:text-amber-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                openStudioRequestDialog(project);
                              }}
                              title="Send Request to Partner"
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-zinc-400 hover:text-orange-400"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/trp/project/${project.id}`);
                              }}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded Payments */}
                      {isExpanded && project.payments.map((payment, idx) => {
                        const isPaid = payment.is_paid;
                        const paymentMonth = payment.month || "";
                        const paymentSelectionId = `${project.id}_${payment.id}`;
                        const isSelected = selectedPayments[paymentSelectionId] || false;
                        
                        return (
                          <TableRow 
                            key={`${project.id}-payment-${payment.id || idx}`} 
                            className={cn(
                              "border-zinc-800/50 bg-zinc-950/50 hover:bg-zinc-900/30",
                              isSelected && "bg-green-900/10"
                            )}
                          >
                            <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                              {!isPaid && (
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => togglePaymentSelection(paymentSelectionId)}
                                  className="border-zinc-600 data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                                />
                              )}
                            </TableCell>
                            <TableCell colSpan={2}>
                              <div className="flex items-center gap-2 pl-2">
                                <FileText className="h-3 w-3 text-zinc-600" />
                                <span className="text-sm text-zinc-300">
                                  {payment.name || payment.notes || `Payment ${idx + 1}`}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Select
                                value={paymentMonth}
                                onValueChange={(v) => updatePaymentMonth(payment.id, v)}
                              >
                                <SelectTrigger className="h-7 w-[100px] bg-zinc-800 border-zinc-700 text-zinc-50 text-xs">
                                  <SelectValue placeholder="Month" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700">
                                  {monthOptions.map(m => (
                                    <SelectItem key={m.value} value={m.value} className="text-zinc-50 text-xs">
                                      {m.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-zinc-400">
                              {formatOriginal(payment.amount, currency)}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-green-400/70">
                              {isPaid ? formatOriginal(payment.amount, currency) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm text-orange-400/70">
                              {!isPaid ? formatOriginal(payment.amount, currency) : "—"}
                            </TableCell>
                            <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-1">
                                <Switch
                                  checked={isPaid}
                                  onCheckedChange={(checked) => togglePaymentPaid(payment.id, checked)}
                                  className="data-[state=checked]:bg-green-600 scale-75"
                                />
                                {isPaid ? (
                                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                                ) : (
                                  <Clock className="h-3 w-3 text-zinc-500" />
                                )}
                              </div>
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              {/* Split button - only for unpaid payments without split group */}
                              {!isPaid && !payment.payment_group_id && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenSplitDialog(payment)}
                                  className="h-6 w-6 p-0 text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                                  title="Split into multiple payments"
                                >
                                  <Split className="h-3 w-3" />
                                </Button>
                              )}
                              {payment.payment_group_id && (
                                <Badge className="text-[9px] h-4 bg-purple-900/30 text-purple-400 border-purple-700">Split</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Split Payment Dialog */}
      <Dialog open={splitDialogOpen} onOpenChange={setSplitDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Split className="h-5 w-5 text-purple-400" />
              Split Payment
            </DialogTitle>
          </DialogHeader>
          
          {splitService && (
            <div className="space-y-4">
              <div className="text-sm text-zinc-400">
                Splitting: <span className="text-zinc-50 font-medium">{splitService.name}</span>
                <br />
                Total: <span className="text-green-400 font-mono">${splitService.amount?.toLocaleString()}</span>
              </div>
              
              <div className="space-y-3">
                {splitParts.map((part, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-zinc-800/50 rounded">
                    <span className="text-xs text-zinc-500 w-6">#{idx + 1}</span>
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-[10px] text-zinc-500">%</Label>
                        <Input
                          type="number"
                          value={part.percent}
                          onChange={(e) => updateSplitPart(idx, "percent", e.target.value)}
                          className="h-7 bg-zinc-800 border-zinc-700 text-zinc-50 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-zinc-500">Amount</Label>
                        <Input
                          type="number"
                          value={part.amount}
                          onChange={(e) => updateSplitPart(idx, "amount", e.target.value)}
                          className="h-7 bg-zinc-800 border-zinc-700 text-zinc-50 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-zinc-500">Month</Label>
                        <Select value={part.month} onValueChange={(v) => updateSplitPart(idx, "month", v)}>
                          <SelectTrigger className="h-7 bg-zinc-800 border-zinc-700 text-zinc-50 text-xs">
                            <SelectValue placeholder="Month" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-800 border-zinc-700">
                            {monthOptions.map(m => (
                              <SelectItem key={m.value} value={m.value} className="text-zinc-50 text-xs">
                                {m.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {splitParts.length > 2 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeSplitPart(idx)}
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="flex justify-between items-center text-xs">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addSplitPart}
                  className="h-7 text-xs border-zinc-700 text-zinc-400"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Part
                </Button>
                <span className={cn(
                  "font-mono",
                  Math.abs(splitParts.reduce((s, p) => s + p.amount, 0) - (splitService?.amount || 0)) > 1
                    ? "text-red-400"
                    : "text-green-400"
                )}>
                  Total: ${splitParts.reduce((s, p) => s + p.amount, 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSplitDialogOpen(false)}
              className="border-zinc-700 text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSplit}
              disabled={splitting}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {splitting ? "Splitting..." : `Split into ${splitParts.length} Payments`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Generation Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-green-400" />
              Создать TRP Invoice
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Selected payments summary */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-sm text-zinc-400">Выбрано платежей: <span className="text-zinc-50 font-medium">{Object.values(selectedPayments).filter(v => v).length}</span></p>
              <p className="text-lg text-green-400 font-mono font-bold mt-1">${selectedTotal.toLocaleString()} USD</p>
            </div>
            
            {/* Info */}
            <div className="text-xs text-zinc-500 space-y-1">
              <p>• Получатель: TRP Partner (из проекта)</p>
              <p>• Отправитель: SmartOrange LLC (US)</p>
              <p>• Валюта: USD</p>
              <p>• Инвойс будет скачан как PDF</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInvoiceDialogOpen(false)}
              className="border-zinc-700"
            >
              Отмена
            </Button>
            <Button
              onClick={handleGenerateInvoice}
              disabled={generatingInvoice}
              className="bg-green-600 hover:bg-green-700"
            >
              {generatingInvoice ? (
                <>Генерация...</>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Скачать Invoice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stripe Payment Link Dialog */}
      <Dialog open={stripeDialogOpen} onOpenChange={setStripeDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-400" />
              Ссылка на оплату - {lastInvoiceNumber}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!stripeCheckoutUrl ? (
              <>
                <p className="text-zinc-400 text-sm">
                  Создайте ссылку для оплаты через Stripe. Партнёр увидит список сервисов и сможет оплатить картой.
                </p>
                <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <p className="text-xs text-purple-300">
                    К сумме будет добавлено 3% Processing Fee
                  </p>
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <Label className="text-zinc-300">Payment Link:</Label>
                <div className="flex gap-2">
                  <Input
                    value={stripeCheckoutUrl}
                    readOnly
                    className="bg-zinc-800 border-zinc-700 text-zinc-50 font-mono text-xs"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => copyStripeLink(stripeCheckoutUrl)}
                    className="border-zinc-700 shrink-0"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  variant="outline"
                  className="w-full border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                  onClick={() => window.open(stripeCheckoutUrl, "_blank")}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Открыть страницу оплаты
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setStripeDialogOpen(false);
                setStripeCheckoutUrl("");
                setLastInvoiceId(null);
              }}
              className="border-zinc-700"
            >
              Закрыть
            </Button>
            {!stripeCheckoutUrl && (
              <Button
                onClick={handleCreateStripeLink}
                disabled={generatingStripeLink}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {generatingStripeLink ? (
                  <>Создание...</>
                ) : (
                  <>
                    <Link2 className="h-4 w-4 mr-2" />
                    Создать ссылку
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Studio Request Dialog - Send request to Partner */}
      <Dialog open={studioRequestDialogOpen} onOpenChange={setStudioRequestDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Send className="h-5 w-5 text-amber-400" />
              Send Request to Partner
            </DialogTitle>
          </DialogHeader>
          
          {studioRequestProject && (
            <div className="space-y-4 mt-2">
              <div className="p-3 bg-zinc-800/50 rounded-lg">
                <p className="text-xs text-zinc-500">Project</p>
                <p className="text-sm text-zinc-50 font-medium">{studioRequestProject.name}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 mb-1.5 block text-xs">Request Type</Label>
                  <Select 
                    value={studioRequestForm.request_type} 
                    onValueChange={(v) => setStudioRequestForm(f => ({ ...f, request_type: v }))}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="price_adjustment">Price Adjustment</SelectItem>
                      <SelectItem value="rework">Rework Request</SelectItem>
                      <SelectItem value="additional_work">Additional Work</SelectItem>
                      <SelectItem value="deadline_change">Deadline Change</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-zinc-400 mb-1.5 block text-xs">Priority</Label>
                  <Select 
                    value={studioRequestForm.priority} 
                    onValueChange={(v) => setStudioRequestForm(f => ({ ...f, priority: v }))}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-zinc-400 mb-1.5 block text-xs">Title *</Label>
                <Input
                  value={studioRequestForm.title}
                  onChange={(e) => setStudioRequestForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Brief title for the request"
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>
              
              <div>
                <Label className="text-zinc-400 mb-1.5 block text-xs">Description</Label>
                <Textarea
                  value={studioRequestForm.description}
                  onChange={(e) => setStudioRequestForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Detailed description of what you need..."
                  className="bg-zinc-800 border-zinc-700 min-h-[80px]"
                />
              </div>
              
              {(studioRequestForm.request_type === "price_adjustment" || 
                studioRequestForm.request_type === "additional_work") && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-zinc-400 mb-1.5 block text-xs">Price per unit ($)</Label>
                    <Input
                      type="number"
                      value={studioRequestForm.price_per_unit}
                      onChange={(e) => setStudioRequestForm(f => ({ ...f, price_per_unit: e.target.value }))}
                      placeholder="0.00"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 mb-1.5 block text-xs">Quantity</Label>
                    <Input
                      type="number"
                      value={studioRequestForm.quantity}
                      onChange={(e) => setStudioRequestForm(f => ({ ...f, quantity: e.target.value }))}
                      placeholder="1"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 mb-1.5 block text-xs">Discount (%)</Label>
                    <Input
                      type="number"
                      value={studioRequestForm.discount}
                      onChange={(e) => setStudioRequestForm(f => ({ ...f, discount: e.target.value }))}
                      placeholder="0"
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
              )}
              
              {/* Total amount */}
              <div>
                <Label className="text-zinc-400 mb-1.5 block text-xs">
                  Total Amount (USD) {studioRequestForm.price_per_unit && studioRequestForm.quantity ? "(auto)" : ""}
                </Label>
                <Input
                  type="number"
                  value={
                    studioRequestForm.price_per_unit && studioRequestForm.quantity
                      ? (parseFloat(studioRequestForm.price_per_unit) * parseInt(studioRequestForm.quantity) * (1 - (parseFloat(studioRequestForm.discount) || 0) / 100)).toFixed(2)
                      : studioRequestForm.amount
                  }
                  onChange={(e) => setStudioRequestForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  className="bg-zinc-800 border-zinc-700"
                  readOnly={!!(studioRequestForm.price_per_unit && studioRequestForm.quantity)}
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setStudioRequestDialogOpen(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateStudioRequest}
              disabled={submittingStudioRequest || !studioRequestForm.title}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {submittingStudioRequest ? (
                "Sending..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrpSalesPage;
